import React from 'react';
import { PackageOpenIcon, CoinsIcon, MonitorPlayIcon, GraduationCapIcon, ArrowRightIcon, SparklesIcon } from 'lucide-react';
const benefits = [{
  icon: PackageOpenIcon,
  title: "Laboratori Pronti all'Uso",
  desc: 'Istruzioni passo-passo per 50 percorsi artistici unici, dalla 1ª alla 5ª classe.',
  chip: '50 laboratori',
  accent: 'orange'
}, {
  icon: CoinsIcon,
  title: 'Creatività a Basso Costo',
  desc: 'Progetti basati su materiali semplici o di riciclo che hai già a scuola.',
  chip: 'Materiali < 5€',
  accent: 'green'
}, {
  icon: MonitorPlayIcon,
  title: 'Supporto Creativo per la LIM',
  desc: "Slide visuali già pronte per spiegare la teoria dell'arte in modo magico e immediato.",
  chip: 'Slide LIM incluse',
  accent: 'blue'
}, {
  icon: GraduationCapIcon,
  title: "Metodologia d'Eccellenza",
  desc: "Un certificato che attesta l'utilizzo della metodologia didattica avanzata EDULABS™.",
  chip: 'Certificato EDULABS™',
  accent: 'amber'
}];
const accentMap: Record<string, {
  iconBg: string;
  iconBorder: string;
  iconText: string;
  chipBg: string;
  chipText: string;
  glow: string;
  hoverBorder: string;
}> = {
  orange: {
    iconBg: 'from-orange-500/20 to-orange-600/5',
    iconBorder: 'border-orange-500/30',
    iconText: 'text-orange-400',
    chipBg: 'bg-orange-500/10 border-orange-500/30',
    chipText: 'text-orange-400',
    glow: 'rgba(249,115,22,0.18)',
    hoverBorder: 'hover:border-orange-500/50'
  },
  green: {
    iconBg: 'from-emerald-500/20 to-emerald-600/5',
    iconBorder: 'border-emerald-500/30',
    iconText: 'text-emerald-400',
    chipBg: 'bg-emerald-500/10 border-emerald-500/30',
    chipText: 'text-emerald-400',
    glow: 'rgba(16,185,129,0.18)',
    hoverBorder: 'hover:border-emerald-500/50'
  },
  blue: {
    iconBg: 'from-sky-500/20 to-sky-600/5',
    iconBorder: 'border-sky-500/30',
    iconText: 'text-sky-400',
    chipBg: 'bg-sky-500/10 border-sky-500/30',
    chipText: 'text-sky-400',
    glow: 'rgba(56,189,248,0.18)',
    hoverBorder: 'hover:border-sky-500/50'
  },
  amber: {
    iconBg: 'from-amber-500/20 to-amber-600/5',
    iconBorder: 'border-amber-500/30',
    iconText: 'text-amber-400',
    chipBg: 'bg-amber-500/10 border-amber-500/30',
    chipText: 'text-amber-400',
    glow: 'rgba(245,158,11,0.18)',
    hoverBorder: 'hover:border-amber-500/50'
  }
};
export function BenefitsSection() {
  return <section className="relative w-full bg-bg-main py-16 sm:py-24 px-4 overflow-hidden" data-id="element-1">
      {/* Background glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full pointer-events-none" style={{
      background: 'radial-gradient(ellipse, rgba(249,115,22,0.06) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-2"></div>

      <div className="relative max-w-6xl mx-auto z-10" data-id="element-3">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14" data-id="element-4">
          <span className="inline-flex items-center gap-1.5 text-cta-green font-bold text-[11px] sm:text-xs uppercase tracking-[0.15em] bg-cta-green/10 border border-cta-green/30 px-3 py-1.5 rounded-full mb-4" data-id="element-5">
            <SparklesIcon className="w-3.5 h-3.5" data-id="element-6" />
            Cosa otterrai
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-[1.1] mb-4" data-id="element-7">
            Tutto quello che serve per{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-300 via-primary to-orange-500" data-id="element-8">
              insegnare arte
            </span>
            <br className="hidden sm:block" data-id="element-9" /> senza più stress.
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-10">
            Quattro pilastri pensati per farti{' '}
            <span className="text-text-main font-bold" data-id="element-11">risparmiare ore</span> e
            far <span className="text-text-main font-bold" data-id="element-12">brillare</span> la
            tua classe.
          </p>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-10 sm:mb-12" data-id="element-13">
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          const accent = accentMap[benefit.accent];
          return <div key={i} className={`group relative bg-bg-alt rounded-2xl sm:rounded-3xl border border-border-light ${accent.hoverBorder} p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 overflow-hidden`} style={{
            // @ts-ignore — CSS var for hover glow
            '--glow-color': accent.glow
          }} data-id="element-14">
                {/* Hover glow */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
              background: `radial-gradient(circle at top right, ${accent.glow} 0%, transparent 60%)`
            }} aria-hidden="true" data-id="element-15"></div>

                {/* Number badge */}
                <span className="absolute top-5 right-5 text-text-sec/40 font-black text-xs tracking-wider" data-id="element-16">
                  0{i + 1}
                </span>

                <div className="relative z-10" data-id="element-17">
                  {/* Icon */}
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${accent.iconBg} border ${accent.iconBorder} flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[-4deg]`} data-id="element-18">
                    <Icon className={`w-7 h-7 sm:w-8 sm:h-8 ${accent.iconText}`} strokeWidth={2} data-id="element-19" />
                  </div>

                  {/* Chip */}
                  <span className={`inline-flex items-center text-[10px] sm:text-xs font-black uppercase tracking-wider ${accent.chipBg} ${accent.chipText} border px-2.5 py-1 rounded-full mb-3`} data-id="element-20">
                    {benefit.chip}
                  </span>

                  {/* Title */}
                  <h3 className="text-xl sm:text-2xl font-black text-text-main mb-2 leading-tight" data-id="element-21">
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-text-sec leading-relaxed text-sm sm:text-base" data-id="element-22">
                    {benefit.desc}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {/* Closing CTA */}
        <div className="text-center" data-id="element-23">
          <a href="#pricing" className="group inline-flex items-center gap-2 text-text-main hover:text-cta-green font-bold text-base sm:text-lg transition-colors" data-id="element-24">
            <span data-id="element-25">Voglio tutto questo nel mio Kit</span>
            <ArrowRightIcon className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={3} data-id="element-26" />
          </a>
        </div>
      </div>
    </section>;
}